import { getApiErrorMessage } from './api-error.helper';
import { getOrderStatusColor, getOrderStatusLabel } from './open-order.helper';
import { getStatusHistory } from './status-history.helper';

/**
 * There is no FX-specific listing endpoint. FX orders arrive mixed into
 * GET /open-orders (and /orders/history) alongside stock and OTC rows, and this
 * flag is the only way to recognise one.
 */
export function isFxOrder(order: any): boolean {
  return order?.extra?.isFx === true;
}

/**
 * An operator quote: raised from the panel. It normally starts at
 * `pending_confirmation`, waits for the client to accept, and is subject to the
 * 5-minute expiry sweep — but one raised with `requireConfirmation: false` starts at
 * `new`, where neither applies. The flag is not echoed back on the order, so the
 * status is the only tell; `getFxExpiryTime` checks both for exactly that reason.
 */
export function isFxAdminQuote(order: any): boolean {
  return order?.extra?.createdByAdmin === true;
}

/**
 * A self-service conversion the approval threshold held back. It starts at `new`
 * (the client already committed), so it never passes through
 * `pending_confirmation` and the expiry sweep never touches it.
 *
 * The operational consequence is the one to surface: nobody on the desk chose the
 * rate — it is the published FxRate captured at submission — and it stays locked
 * for as long as the order rests. Settle promptly, or cancel if the market moved;
 * there is no re-price.
 */
export function isFxClientApproval(order: any): boolean {
  return order?.extra?.approvalRequired === true;
}

/**
 * Which of the two ways this order came to be. Both land in the same `new` queue,
 * so the operator needs telling apart on the row itself.
 */
export function getFxOriginLabel(order: any): string {
  if (isFxForward(order)) return 'Forward';
  if (isFxClientApproval(order)) return 'Client conversion';
  if (isFxAdminQuote(order)) return 'Operator quote';
  return '-';
}

/**
 * Value-date forward: raised with `forwardDate`, no freeze until that day, settles
 * via poller or manual settle on/after the date.
 */
export function isFxForward(order: any): boolean {
  if (order?.isForward === true || order?.extra?.isForward === true) return true;
  return !!getFxForwardDate(order);
}

/** `YYYY-MM-DD` value date, or null when not a forward. */
export function getFxForwardDate(order: any): string | null {
  const value = order?.forwardDate ?? order?.extra?.forwardDate;
  if (value == null || value === '') return null;
  const text = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
}

/** Local calendar day as `YYYY-MM-DD` — close enough for UI gating; backend is authoritative. */
export function fxLocalYmd(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * True when a forward may be settled (value date reached). Non-forwards are always
 * "due" from the UI's perspective once status is `new`.
 */
export function isFxForwardDue(
  order: any,
  today: string = fxLocalYmd()
): boolean {
  const forwardDate = getFxForwardDate(order);
  if (!forwardDate) return true;
  return today >= forwardDate;
}

/** How long the order has been resting, in ms, or null if `created` is unusable. */
export function getFxAgeMs(order: any, now: number = Date.now()): number | null {
  const created = new Date(order?.created).getTime();
  if (isNaN(created)) return null;
  return Math.max(0, now - created);
}

/** Coarse "2h 14m" / "3m" age, for a column the operator scans rather than reads. */
export function formatFxAge(ms: number | null): string {
  if (ms === null) return '-';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return '<1m';
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

/**
 * FX-only wording. FX orders are stored as ordinary orders and reuse the stock
 * status vocabulary, so `filled` really means "the conversion settled" and `new`
 * means "the client accepted the quoted rate". Keep these out of
 * ORDER_STATUS_LABELS so the stock and OTC tables read as before.
 */
export const FX_STATUS_LABELS: Record<string, string> = {
  pending_confirmation: 'Awaiting Client',
  new: 'Accepted',
  filled: 'Settled',
  cancelled: 'Cancelled',
};

export function getFxStatusLabel(status: string): string {
  return FX_STATUS_LABELS[status] ?? getOrderStatusLabel(status);
}

export function getFxStatusColor(status: string): string {
  // The generic map already reads correctly for every FX status:
  // filled -> success, cancelled -> error, pending_confirmation -> warning, new -> processing.
  return getOrderStatusColor(status);
}

/** The window the backend gives the client to accept before it auto-cancels. */
export const FX_PENDING_TTL_MS = 5 * 60 * 1000;

/**
 * Epoch ms at which a pending order auto-cancels, or null if it cannot expire.
 *
 * The sweep is gated on `extra.createdByAdmin`, so that — not the status — is what
 * decides. A client-raised approval conversion starts at `new` and never expires;
 * checking the status alone would be right only by accident.
 */
export function getFxExpiryTime(order: any): number | null {
  if (!order?.created || order.status !== 'pending_confirmation') return null;
  if (!isFxAdminQuote(order)) return null;
  const created = new Date(order.created).getTime();
  if (isNaN(created)) return null;
  return created + FX_PENDING_TTL_MS;
}

/**
 * True once the window has elapsed. Client-side only: the panel does not poll, so
 * the row still reads `pending_confirmation` until someone hits Refresh. The
 * backend sweep is a cron, so an order can also be a little over 5 minutes old
 * before it actually flips.
 */
export function isFxExpired(order: any, now: number = Date.now()): boolean {
  const expiry = getFxExpiryTime(order);
  return expiry !== null && expiry <= now;
}

/**
 * An expiry lands as `cancelled` with extra.cancelReason "OTHER", which renders as
 * a useless "Other". The status history is what actually distinguishes an expiry
 * from a manual cancel.
 */
export function getFxCancelReasonLabel(order: any): string | null {
  if (order?.status !== 'cancelled') return null;
  const expired = getStatusHistory(order).some(
    (entry) => entry.reason === 'ADMIN_ORDER_EXPIRED'
  );
  return expired
    ? 'Expired — the client did not accept within 5 minutes'
    : null;
}

/**
 * Rates are prices, not amounts, so no currency precision applies. toFixed(8)
 * always produces a decimal point, which makes the trailing-zero trim safe.
 */
export function formatFxRate(
  value: string | number | null | undefined
): string {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (!isFinite(num)) return '-';
  return num.toFixed(8).replace(/\.?0+$/, '');
}

/**
 * The rate the conversion was actually quoted at, unrounded.
 *
 * `order.rate` is stored to six decimals, which on a small cross like RUB→EUR
 * throws most of the rate away: "0.010050" against a real 0.0100502512563, a
 * gap that is visible once multiplied across 1 500 000 RUB. Newer orders carry
 * the full figure in `extra.quotedRate`; older ones do not, and return null.
 */
export function getFxQuotedRate(order: any): string | null {
  const value = order?.extra?.quotedRate;
  if (value === null || value === undefined || value === '') return null;
  const text = toPlainDecimal(value);
  return text !== null && Number(text) > 0 ? text : null;
}

/**
 * Hover text for an FX rate cell: `1 RUB = 0.0100502512563 EUR`.
 *
 * Uses the exact quoted rate when the order has one, and falls back to exactly
 * what the cell showed before — the rounded `order.rate` — for orders that
 * predate `extra.quotedRate`.
 */
export function getFxRateTooltip(order: any): string {
  const rate = getFxQuotedRate(order) ?? formatFxRate(order?.rate);
  return `1 ${order?.fromCurrency} = ${rate} ${order?.toCurrency}`;
}

/**
 * 1 / rate, as the desk quotes it: 0.00273972602739726 (USD per 1 AMD) reads
 * back as 365. toPrecision drops the float noise of the division. Null for a
 * missing, zero or non-numeric rate.
 */
export function formatFxInverseRate(
  rate: string | number | null | undefined
): string | null {
  const num = Number(rate);
  if (rate === null || rate === undefined || rate === '' || !isFinite(num) || num <= 0) {
    return null;
  }
  return formatFxRate(Number((1 / num).toPrecision(12)));
}

/**
 * The readable figure for an order that converts against the desk's direction,
 * e.g. `365 AMD per 1 USD` next to a rate of 0.00273973 (AMD → USD on the
 * USD/AMD desk). Null for an order already in the desk's direction, whose rate
 * reads fine as it is.
 *
 * Inverted when the pair's base is the currency received; rows without a pair
 * fall back to the side, which the backend sets to 'buy' for exactly this case.
 * Inverts the exact quoted rate where there is one - 1 / 0.002740 is 364.96.
 */
export function getFxInverseRateLabel(order: any): string | null {
  const pair: string | undefined = order?.pair;
  const inverted = pair
    ? pair.split('/')[0] === order?.toCurrency
    : order?.side === 'buy';
  if (!inverted) return null;
  const value = formatFxInverseRate(getFxQuotedRate(order) ?? order?.rate);
  return value === null
    ? null
    : `${value} ${order?.fromCurrency} per 1 ${order?.toCurrency}`;
}

/**
 * A number or numeric string as plain positional digits, nothing rounded.
 *
 * `String(n)` is the shortest text that round-trips the double, so it keeps
 * every digit the backend sent — `toFixed` or Intl would pad or round it. The
 * one thing it gets wrong is switching to exponent form below 1e-6, which is
 * expanded here.
 */
function toPlainDecimal(value: string | number): string | null {
  const text = String(value).trim();
  if (!/^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i.test(text)) return null;

  const [mantissa, exponentText] = text.toLowerCase().split('e');
  if (exponentText === undefined) return text;

  const negative = mantissa.startsWith('-');
  const unsigned = mantissa.replace(/^[+-]/, '');
  const [whole, fraction = ''] = unsigned.split('.');
  const digits = `${whole}${fraction}`;
  const point = whole.length + Number(exponentText);

  let plain: string;
  if (point <= 0) {
    plain = `0.${'0'.repeat(-point)}${digits}`;
  } else if (point >= digits.length) {
    plain = `${digits}${'0'.repeat(point - digits.length)}`;
  } else {
    plain = `${digits.slice(0, point)}.${digits.slice(point)}`;
  }
  plain = plain.replace(/^0+(?=\d)/, '');
  return negative ? `-${plain}` : plain;
}

/**
 * Branch on `type` — never on `message` (not stable) and never on `code` (552 is
 * shared by NOT_ENOUGH_FUNDS, ACCOUNT_INACTIVE, ERROR_VALIDATION and others).
 */
export const FX_ERROR_MESSAGES: Record<string, string> = {
  USER_NOT_FOUND: 'This client no longer exists.',
  ACCOUNT_INACTIVE:
    'The client account is inactive — activate it before raising an FX order.',
  TRADING_BLOCKED: 'Trading is blocked for this client.',
  KYC_NOT_APPROVED: 'The client has not passed KYC yet.',
  PERMISSION_DENIED: 'Your role is not allowed to perform this action.',
  ORDER_NOT_FOUND: 'This order no longer exists.',
  ORDER_ALREADY_CANCELLED: 'This order has already been cancelled.',
};

/**
 * `need` / `available` are floats in the `from` currency. The same bag also carries
 * `symbol` and `side`, which describe the canonical pair rather than the
 * conversion — never render those.
 *
 * `precision` is a parameter rather than a CurrencyCacheService lookup: services
 * import the helpers barrel, so importing a service from here would be a cycle.
 */
export function getFxNotEnoughFundsMessage(
  data: any,
  fromCurrency: string,
  precision = 2
): string {
  const need = Number(data?.need);
  const available = Number(data?.available);
  if (!isFinite(need) || !isFinite(available)) {
    return `The client does not have enough ${fromCurrency}.`;
  }
  // The gap is what the operator acts on, so state it rather than leaving them to
  // subtract two numbers.
  const short = Math.max(0, need - available);
  return (
    `Not enough ${fromCurrency}. ` +
    `Needs ${need.toFixed(precision)}, available ${available.toFixed(
      precision
    )} — short ${short.toFixed(precision)} ${fromCurrency}`
  );
}

export function getFxErrorMessage(
  err: any,
  fallback: string,
  ctx?: { fromCurrency?: string; precision?: number }
): string {
  const type = err?.type;
  if (type === 'NOT_ENOUGH_FUNDS') {
    return getFxNotEnoughFundsMessage(
      err?.data,
      ctx?.fromCurrency ?? '',
      ctx?.precision ?? 2
    );
  }
  // ERROR_VALIDATION messages name the offending field, so show them verbatim.
  if (type && type !== 'ERROR_VALIDATION' && FX_ERROR_MESSAGES[type]) {
    return FX_ERROR_MESSAGES[type];
  }
  return getApiErrorMessage(err, fallback);
}

/**
 * On settle/cancel these three almost always mean the row moved on under the
 * operator (the client accepted, the client cancelled, or the 5-minute window
 * expired). Refetch and re-render rather than surfacing a backend string nobody
 * can act on.
 */
export const FX_STALE_ERROR_TYPES = [
  'ORDER_NOT_FOUND',
  'ERROR_VALIDATION',
  'ORDER_ALREADY_CANCELLED',
];

export function isFxStaleOrderError(err: any): boolean {
  return FX_STALE_ERROR_TYPES.includes(err?.type);
}
